import React, {useEffect} from 'react';
import {useShallow} from 'zustand/shallow';
import {useSearchParams} from 'react-router-dom';
import {MapboxFeature} from '@/types/mapbox';
import assertUnreachable from '@/utils/assertUnreachable';
import parseMapboxLocation from '@/utils/parseMapboxLocation';
import {NameAndBirthdate, Location} from './components/steps';
import {STEPS} from './onboardingSteps';
import {useOnboardingStore} from './OnboardingStore';
import GenderSelect from './steps/GenderSelect';
import Portfolio from './steps/Portfolio';
import Bio from './steps/Bio';
import SegmentSelect from './steps/SegmentSelect';
import SpecialtiesSelect from './steps/SpecialtiesSelect';
import CapabilitiesSelect from './steps/CapabilitiesSelect';
import Reonboarding from './steps/Reonboarding';
import ConnectPlatformsContent from './components/connect-platforms/ConnectPlatformsContent';
import authenticateFacebook from '@/services/facebook/Facebook';
import authenticateTiktok from '@/services/tiktok_creator_market_place/TiktokCreatorMarketPlace';
import authenticateYouTube from '@/services/youtube/Youtube';
import Sentry from '@/services/sentry/Sentry';
import {Platform} from '@/types/platforms';
import {useUserData} from '@/hooks/useUserData';
import {useOnboardingAnalytics} from './useOnboardingAnalytics';
import InstagramInstructions from './components/connect-platforms/InstagramInstructions';
import useAutoBio from './useAutoBio';
import OnboardingCompletion from './steps/OnboardingCompletion';
import translate from '@/utils/translate';
import {IOS_APP_RELEASE_DATE} from './onboardingConstants';
import {isBirthdateValid, meetsAgeRequirement} from '@/utils/calculateAge';
import CollaborationRateSelect from '@/views/onboarding/steps/CollaborationRateSelect';

function useResetStepValues() {
  const [setGender, setSegment, step] = useOnboardingStore(
    useShallow((store) => [store.setGender, store.setSegment, store.step])
  );

  useEffect(() => {
    if (step === STEPS.GENDER) {
      setGender(undefined);
    }
    if (step === STEPS.CREATOR_SEGMENT) {
      setSegment(undefined);
    }
  }, [step, setGender, setSegment]);
}

function useRenderStepContent(onNext?: () => Promise<void>) {
  const {step, setPlatformError, setStep, analyticsFlowId, flowName} =
    useOnboardingStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const {user} = useUserData({});
  useResetStepValues();

  const {
    sendSignUpScreenButtonPressedEvent,
    sendSignUpStepStartedEvent,
    sendSignUpStepEndedEvent,
  } = useOnboardingAnalytics(analyticsFlowId ?? '', flowName ?? '');

  const handleConnectPlatform = async (platform: Platform) => {
    sendSignUpScreenButtonPressedEvent(`connect-${platform}`, step);
    sendSignUpStepStartedEvent(platform);
    if (platform === Platform.Instagram) {
      setStep(STEPS.INSTAGRAM_INSTRUCTIONS);
      return;
    }
    try {
      const redirectQueryParams = {
        step: STEPS.SYNC_PLATFORMS,
      };

      switch (platform) {
        case Platform.TikTok:
          await authenticateTiktok(user?.id ?? null, redirectQueryParams);
          break;
        case Platform.YouTube:
          await authenticateYouTube(user?.id ?? null, redirectQueryParams);
          break;
        case Platform.Facebook:
          await authenticateFacebook(user?.id ?? null, redirectQueryParams);
          break;
        default:
          throw new Error(`Platform ${platform} is not supported.`);
      }
    } catch (err) {
      sendSignUpStepEndedEvent(platform, 'error', JSON.stringify(err));
      setPlatformError(`Failed to connect to ${platform}. Please try again.`);
      Sentry.captureException(
        {
          message: `Attempt to connect to unsupported platform: ${platform} failed`,
          extra: {
            platform,
            error: err,
          },
        },
        {
          level: 'error',
        }
      );
    }
  };

  const handleClosePlatformError = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('success');
    newParams.delete('message');
    newParams.delete('provider');
    setSearchParams(newParams);

    setPlatformError(undefined);
  };

  useAutoBio();

  const [
    firstName,
    lastName,
    birthdate,
    segment,
    location,
    gender,
    bio,
    specialties,
    capabilities,
    profilePictureUrl,
    portfolioMediaItems,
    minCollaborationRate,
  ] = useOnboardingStore(
    useShallow((store) => [
      store.firstName,
      store.lastName,
      store.birthdate,
      store.segment,
      store.location,
      store.gender,
      store.bio,
      store.specialties,
      store.capabilities,
      store.profilePictureUrl,
      store.portfolioMediaItems,
      store.minCollaborationRate,
    ])
  );

  const [
    setFirstName,
    setLastName,
    setBirthdate,
    setSegment,
    setLocation,
    setLocale,
    setGender,
    setBio,
    setSpecialities,
    setCapabilities,
    setProfilePictureUrl,
    setPortfolioMediaItems,
    setMinCollaborationRate,
    openBanner,
    closeBanner,
  ] = useOnboardingStore(
    useShallow((store) => [
      store.setFirstName,
      store.setLastName,
      store.setBirthdate,
      store.setSegment,
      store.setLocation,
      store.setLocale,
      store.setGender,
      store.setBio,
      store.setSpecialties,
      store.setCapabilities,
      store.setProfilePictureUrl,
      store.setPortfolioMediaItems,
      store.setMinCollaborationRate,
      store.openBanner,
      store.closeBanner,
    ])
  );

  switch (step) {
    case STEPS.NAME_AND_BIRTHDATE:
      return (
        <NameAndBirthdate
          firstNameValue={firstName}
          lastNameValue={lastName}
          onChangeFirstName={setFirstName}
          onChangeLastName={setLastName}
          birthdateValue={birthdate}
          onChangeBirthdate={(date) => {
            setBirthdate(date);
            if (isBirthdateValid(date)) {
              if (!meetsAgeRequirement(date)) {
                openBanner({
                  title: translate('Popular Pays is for creators 18+'),
                  children: translate(
                    'Come back then—we can’t wait to have you!'
                  ),
                  severity: 'error',
                });
              } else {
                closeBanner();
              }
            }
          }}
        />
      );
    case STEPS.CREATOR_SEGMENT:
      return (
        <SegmentSelect
          selectedSegment={segment}
          onSegmentChange={async (newSegment) => {
            setSegment(newSegment);
            await onNext?.();
          }}
        />
      );
    case STEPS.LOCATION:
      return (
        <Location
          onChangeLocation={(selectedLocation: MapboxFeature | null) => {
            if (!selectedLocation) {
              setLocation(undefined);
              setLocale(undefined);
              return;
            }

            const parsed = parseMapboxLocation(selectedLocation);
            const englishPlaceName = selectedLocation.place_name_en;

            setLocation(englishPlaceName ?? selectedLocation.place_name);
            setLocale({
              id: selectedLocation.id,
              apiUid: parsed.apiUid,
              city: parsed.city,
              state: parsed.state ?? null,
              country: parsed.country ?? '',
              latitude: parsed.latitude,
              longitude: parsed.longitude,
            });
          }}
        />
      );
    case STEPS.GENDER:
      return (
        <GenderSelect
          selectedGender={gender}
          onGenderChange={async (newGender) => {
            setGender(newGender);
            await onNext?.();
          }}
        />
      );
    case STEPS.COLLABORATION_RATE:
      return (
        <CollaborationRateSelect
          selectedRate={minCollaborationRate}
          onRateChange={setMinCollaborationRate}
        />
      );
    case STEPS.SPECIALTIES:
      return (
        <SpecialtiesSelect
          selectedSpecialties={specialties}
          onSpecialtiesChange={setSpecialities}
        />
      );
    case STEPS.CAPABILITIES:
      return (
        <CapabilitiesSelect
          selectedCapabilities={capabilities}
          onCapabilitiesChange={setCapabilities}
        />
      );
    case STEPS.BIO:
      return <Bio value={bio} onChange={setBio} />;
    case STEPS.PORTFOLIO: {
      const fullName = `${firstName} ${lastName}`.trim();
      return (
        <Portfolio
          fullName={fullName}
          birthdate={birthdate}
          location={location}
          specialties={Array.from(specialties)}
          profilePictureUrl={profilePictureUrl}
          setProfilePictureUrl={setProfilePictureUrl}
          portfolioMediaItems={portfolioMediaItems}
          setPortfolioMediaItems={setPortfolioMediaItems}
        />
      );
    }
    case STEPS.COMPLETION:
      return <OnboardingCompletion />;
    case STEPS.SYNC_PLATFORMS:
      return (
        <ConnectPlatformsContent
          onConnectPlatform={handleConnectPlatform}
          onCloseError={handleClosePlatformError}
        />
      );
    case STEPS.INSTAGRAM_INSTRUCTIONS:
      return <InstagramInstructions />;
    case STEPS.REONBOARDING: {
      const userSeenOnboarding = user?.createdAt
        ? user.createdAt > IOS_APP_RELEASE_DATE
        : false;
      const title = userSeenOnboarding
        ? translate('Complete your profile')
        : translate('Update your profile');
      const subtitle = userSeenOnboarding
        ? translate(
            'Finish the last few steps to get paid gigs with top brands.'
          )
        : translate(
            'Give it an upgrade to increase your opportunities so brands can find you faster!'
          );
      return <Reonboarding title={title} subtitle={subtitle} />;
    }
    default:
      return assertUnreachable(step);
  }
}

export default useRenderStepContent;
